import React, {
  LegacyRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { TextInput } from "@/components/atoms/TextInput";

import CustomPromptView from "./CustomPromptView";

import type { TextInputProps } from "@/components/atoms/TextInput";

/**
 * Props for prompt data state
 */
interface DataProps {
  /** Prompt dialog title */
  title: string;
  /** Optional message content to display below title */
  message?: string;
  /** Callback invoked with user input when prompt is completed — may be async */
  callback?: (text: string) => void | Promise<unknown>;
}

/**
 * Imperative methods exposed via ref for CustomPrompt component
 */
export interface CustomPromptRefProps {
  /** Function to display the prompt dialog with title, message, and completion callback */
  prompt: (
    title: string,
    message?: string,
    callback?: (text: string) => void | Promise<unknown>
  ) => void;
}

/**
 * Container component for custom prompt dialog with imperative API
 * Manages prompt state and handles user input with callback on completion
 * @param root0 - Component props (unused, accepts ref only)
 * @param root0._ - Unused props parameter
 * @param root0.ref - Ref to expose imperative prompt API
 */
const CustomPromptContainer = React.forwardRef<CustomPromptRefProps>(
  (_, ref) => {
    const inputRef = useRef<React.ComponentRef<typeof TextInput> | null>(null);
    const [data, setData] = useState<DataProps | undefined>(undefined);
    const [valueInput, setValueInput] = useState<string>("");
    const [isOpen, setIsOpen] = useState<boolean>(false);
    const [isLoading, setIsLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string | undefined>(
      undefined
    );

    const handlePrompt = (
      title: string,
      message?: string,
      callback?: (text: string) => void | Promise<unknown>
    ) => {
      setData({ title, message, callback });
      setIsOpen(true);
    };

    const handleReset = useCallback(() => {
      setData(undefined);
      setValueInput("");
      setErrorMessage(undefined);
    }, []);

    const handleClose = useCallback(() => {
      handleReset();
      setIsOpen(false);
    }, [handleReset]);

    const handleDone = useCallback(() => {
      if (!valueInput) return;
      setErrorMessage(undefined);
      const result = data?.callback?.(valueInput);
      if (result && typeof (result as Promise<unknown>).then === "function") {
        setIsLoading(true);
        (result as Promise<unknown>)
          .then(() => {
            setIsLoading(false);
            handleClose();
          })
          .catch((error: unknown) => {
            setIsLoading(false);
            const msg =
              error instanceof Error ? error.message : "Something went wrong";
            setErrorMessage(msg);
          });
      } else {
        handleClose();
      }
    }, [data, handleClose, valueInput]);

    useImperativeHandle(
      ref,
      () => ({
        prompt: handlePrompt,
      }),
      []
    );

    useEffect(() => {
      if (isOpen) {
        setTimeout(() => inputRef.current?.focus(), 500);
      }
    }, [isOpen]);

    return (
      <CustomPromptView
        inputRef={inputRef as LegacyRef<TextInputProps>}
        isOpen={isOpen}
        title={data?.title ?? ""}
        message={data?.message}
        value={valueInput}
        isLoading={isLoading}
        errorMessage={errorMessage}
        onChangeText={setValueInput}
        onClose={handleClose}
        onDone={handleDone}
      />
    );
  }
);

CustomPromptContainer.displayName = "CustomPromptContainer";

export default CustomPromptContainer;
